import {useState} from 'react'

import {difference, intersection, uniq} from 'es-toolkit'

const useSelectedIds = <IdType>(getIds: () => Array<IdType>, initialIds: Array<IdType> = []) => {
  const [selectedIds, setSelectedIds] = useState(initialIds)

  const allIds = getIds()
  // Always filter ids for whether they actually exist in the getIds array.
  const filteredSelectedIds = intersection(selectedIds, allIds)
  const isSelected = (id: IdType) => filteredSelectedIds.includes(id)

  const isAnySelected = (ids: Array<IdType> = allIds) =>
    difference(ids, filteredSelectedIds).length < ids.length

  const areAllSelected = (ids: Array<IdType> = allIds) =>
    difference(ids, filteredSelectedIds).length === 0

  const addIds = (ids: Array<IdType>) => {
    setSelectedIds(uniq([...filteredSelectedIds, ...ids]))
  }

  const removeIds = (ids: Array<IdType>) => {
    setSelectedIds(difference(filteredSelectedIds, ids))
  }

  const toggleId = (id: IdType) => {
    if (isSelected(id)) {
      removeIds([id])
    } else {
      addIds([id])
    }
  }

  const toggleAll = (ids: Array<IdType> = allIds) => {
    if (isAnySelected(ids)) {
      removeIds(ids)
    } else {
      addIds(ids)
    }
  }

  return {
    isSelected,
    isAnySelected,
    areAllSelected,
    addIds,
    removeIds,
    toggleId,
    toggleAll,
    selectedIds: filteredSelectedIds,
  }
}

export default useSelectedIds
